import { MAX_GROCERY_ITEMS_PER_LIST, MAX_GROCERY_LISTS_PER_HOUSEHOLD } from '@/constants/limits';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/use-auth';
import { useProfile } from '@/hooks/useProfile';
import {
  getCachedLists,
  getCachedList,
  setCachedLists,
  setCachedList,
  invalidateGroceryListsCache,
  removeCachedList,
  updateCachedListInLists,
} from '@/lib/grocery-list-cache';
import { removeCachedProfile } from '@/lib/profile-cache';
import type { GroceryList, GroceryItem } from '@/lib/grocery-list-cache';
import type { Recipe } from '@/lib/recipes/types';
import { aggregateIngredients } from '@/utils/ingredientMatcher';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

/** Dedupe concurrent refetches for the same household so only one network request runs */
let inFlightHouseholdId: string | null = null;
let inFlightPromise: Promise<void> | null = null;

function defaultCategoryForItem(name: string): string {
  const lower = name.toLowerCase();
  if (/milk|cheese|butter|yogurt|cream|egg/.test(lower)) return 'Dairy';
  if (/chicken|beef|pork|fish|meat/.test(lower)) return 'Meat';
  if (/bread|bun|roll/.test(lower)) return 'Bakery';
  if (/onion|garlic|pepper|tomato|lettuce|carrot|basil|ginger/.test(lower)) return 'Produce';
  if (/frozen/.test(lower)) return 'Frozen';
  return 'Pantry';
}

export type { GroceryList, GroceryItem };

/** Raw grocery_lists row from Supabase */
interface GroceryListRow {
  id: string;
  household_id: string;
  name: string;
  items: unknown;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

function parseItems(raw: unknown): GroceryItem[] {
  if (!Array.isArray(raw)) return [];
  const addedAtToNum = (v: unknown) =>
    typeof v === 'number' ? v : new Date(String(v ?? 0)).getTime();
  const parsed = raw.map((item, index) => {
    const o = item as Record<string, unknown>;
    const base = {
      id: String(o.id ?? ''),
      name: String(o.name ?? ''),
      category: String(o.category ?? 'other'),
      checked: Boolean(o.checked),
      recipeId: o.recipeId != null ? String(o.recipeId) : undefined,
      addedAt: addedAtToNum(o.addedAt),
      position: typeof o.position === 'number' ? o.position : index,
    };
    if (o.normalizedName != null) (base as GroceryItem).normalizedName = String(o.normalizedName);
    if (o.quantity != null && typeof o.quantity === 'number') (base as GroceryItem).quantity = o.quantity;
    if (o.unit != null) (base as GroceryItem).unit = String(o.unit);
    if (Array.isArray(o.sourceRecipes)) (base as GroceryItem).sourceRecipes = o.sourceRecipes.map(String);
    return base as GroceryItem;
  });
  parsed.sort(
    (a, b) => a.position - b.position || a.addedAt - b.addedAt || a.id.localeCompare(b.id)
  );
  return parsed;
}

function rowToList(row: GroceryListRow): GroceryList {
  return {
    id: row.id,
    name: row.name,
    items: parseItems(row.items),
    createdAt: new Date(row.created_at).getTime(),
  };
}

function listToRowPayload(list: Partial<GroceryList>): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };
  if (list.name !== undefined) payload.name = list.name;
  if (list.items !== undefined) {
    payload.items = list.items.map((item, index) => {
      const row: Record<string, unknown> = {
        id: item.id,
        name: item.name,
        category: item.category,
        checked: item.checked,
        recipeId: item.recipeId ?? null,
        addedAt: item.addedAt,
        position: index,
      };
      if (item.normalizedName != null) row.normalizedName = item.normalizedName;
      if (item.quantity != null) row.quantity = item.quantity;
      if (item.unit != null) row.unit = item.unit;
      if (item.sourceRecipes != null && item.sourceRecipes.length > 0) row.sourceRecipes = item.sourceRecipes;
      return row;
    });
  }
  return payload;
}

/**
 * Fetch the current household's grocery lists.
 * Uses cache; only fetches when cache is empty or invalidated.
 */
export function useGroceryLists() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const householdId = profile?.household_id ?? null;
  const [lists, setLists] = useState<GroceryList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(
    async (forceNetwork = false, householdIdOverride?: string | null) => {
      const effectiveHouseholdId = householdIdOverride !== undefined ? householdIdOverride : householdId;
      if (!user?.id) {
        setLoading(false);
        return;
      }
      if (effectiveHouseholdId == null) {
        setLists([]);
        setLoading(false);
        return;
      }

      const cached = getCachedLists(effectiveHouseholdId);
      if (cached !== null && !forceNetwork) {
        setLists(cached);
        setLoading(false);
        return;
      }

      // If another hook instance is already fetching for this household, wait for it and use cache
      if (!forceNetwork && inFlightHouseholdId === effectiveHouseholdId && inFlightPromise) {
        try {
          await inFlightPromise;
          const after = getCachedLists(effectiveHouseholdId);
          if (after !== null) {
            setLists(after);
          }
        } catch {
          // First fetch failed; leave error state to the instance that did the fetch
        } finally {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);
      inFlightHouseholdId = effectiveHouseholdId;
      inFlightPromise = (async () => {
        try {
          const { data, error: fetchError } = await supabase
            .from('grocery_lists')
            .select('*')
            .eq('household_id', effectiveHouseholdId)
            .order('updated_at', { ascending: false });

          if (fetchError) throw fetchError;
          const result = (data ?? []).map((r) => rowToList(r as GroceryListRow));
          setCachedLists(effectiveHouseholdId, result);
        } finally {
          inFlightHouseholdId = null;
          inFlightPromise = null;
        }
      })();

      try {
        await inFlightPromise;
        const result = getCachedLists(effectiveHouseholdId);
        if (result !== null) setLists(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    },
    [user?.id, householdId]
  );

  // Sync from cache synchronously so we don't show loading or trigger fetch when cache is warm
  useLayoutEffect(() => {
    if (!householdId) {
      setLists([]);
      setLoading(false);
      return;
    }
    const cached = getCachedLists(householdId);
    if (cached !== null) {
      setLists(cached);
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    if (!user?.id || !householdId) {
      setLists([]);
      setLoading(false);
      return;
    }
    refetch();
  }, [user?.id, householdId, refetch]);

  const deleteList = useCallback(
    async (id: string): Promise<void> => {
      const previousLists = [...lists];
      setLists((prev) => prev.filter((l) => l.id !== id));
      setError(null);
      try {
        const { error: deleteError } = await supabase
          .from('grocery_lists')
          .delete()
          .eq('id', id);
        if (deleteError) throw deleteError;
        invalidateGroceryListsCache();
        removeCachedList(id);
      } catch (err) {
        setLists(previousLists);
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [lists]
  );

  return { lists, loading, error, refetch, deleteList };
}

// For a single list with real-time sync, use useGroceryList(listId) from '@/hooks/useGroceryList'.

/** Return type when a list is created; newHouseholdId is set when we auto-created a household. */
export type CreateListResult = { list: GroceryList; newHouseholdId?: string };

/**
 * Mutation: create a new grocery list.
 * If the user has no household, creates one first (default name "My Household") then creates the list.
 * Returns { list, newHouseholdId? } so the caller can refetch with the new household id (refetch(true, result.newHouseholdId)) and avoid stale UI.
 */
export function useCreateList() {
  const { user } = useAuth();
  const { profile, refreshProfile } = useProfile();
  const householdId = profile?.household_id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createList = useCallback(
    async (name: string): Promise<CreateListResult | null> => {
      if (!user?.id) {
        setError(new Error('You must be signed in to create a list'));
        return null;
      }

      setLoading(true);
      setError(null);

      let effectiveHouseholdId = householdId;
      let createdHouseholdId: string | undefined;
      if (!effectiveHouseholdId) {
        try {
          const { data: newHousehold, error: createHError } = await supabase.rpc(
            'create_household_for_user',
            { household_name: 'My Household' } as { household_name: string }
          );
          if (createHError) throw createHError;
          const id = (newHousehold as { id?: string } | null)?.id;
          if (!id) throw new Error('Could not create household');
          effectiveHouseholdId = id;
          createdHouseholdId = id;
          removeCachedProfile(user.id);
          await refreshProfile();
          invalidateGroceryListsCache();
        } catch (err) {
          const e = err instanceof Error ? err : new Error(String(err));
          setError(e);
          setLoading(false);
          throw e;
        }
      }

      try {
        const { count, error: countError } = await supabase
          .from('grocery_lists')
          .select('*', { count: 'exact', head: true })
          .eq('household_id', effectiveHouseholdId);

        if (countError) throw countError;
        if ((count ?? 0) >= MAX_GROCERY_LISTS_PER_HOUSEHOLD) {
          const e = new Error(
            `Maximum of ${MAX_GROCERY_LISTS_PER_HOUSEHOLD} lists. Delete a list to create a new one.`
          );
          setError(e);
          throw e;
        }

        const { data, error: insertError } = await supabase
          .from('grocery_lists')
          .insert({
            household_id: effectiveHouseholdId,
            name: name || 'Shopping List',
            items: [],
            created_by_user_id: user.id,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        invalidateGroceryListsCache();
        const list = data ? rowToList(data as GroceryListRow) : null;
        if (!list) return null;
        return createdHouseholdId ? { list, newHouseholdId: createdHouseholdId } : { list };
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [user?.id, householdId, refreshProfile]
  );

  return { createList, loading, error };
}

/**
 * Mutation: update a grocery list (name and/or items).
 */
export function useUpdateList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateList = useCallback(
    async (id: string, updates: Partial<Pick<GroceryList, 'name' | 'items'>>): Promise<GroceryList | null> => {
      setLoading(true);
      setError(null);
      try {
        const payload = listToRowPayload(updates);
        const { data, error: updateError } = await supabase
          .from('grocery_lists')
          .update(payload)
          .eq('id', id)
          .select()
          .single();

        if (updateError) throw updateError;
        const updated = data ? rowToList(data as GroceryListRow) : null;
        if (updated) {
          setCachedList(updated);
          updateCachedListInLists(updated);
        }
        return updated;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { updateList, loading, error };
}

export interface AddRecipeToGroceryListResult {
  addedCount: number;
  mergedCount: number;
  /** Items before the add (for undo) */
  previousItems: GroceryItem[];
}

/**
 * Add a recipe's selected ingredients to a grocery list with smart aggregation:
 * normalizes names, matches existing items, checks unit compatibility, aggregates quantities, tracks source recipes.
 * Returns result with previousItems so caller can implement undo.
 */
export function useAddRecipeToGroceryList() {
  const { updateList, loading: updateLoading, error: updateError } = useUpdateList();

  const addRecipeToGroceryList = useCallback(
    async (
      listId: string,
      recipe: Recipe,
      selectedIngredientIndices: number[],
      /** When provided, use these strings instead of recipe.ingredients (e.g. scaled by serving size). Length/order must match selectedIndices. */
      scaledIngredientStrings?: string[]
    ): Promise<AddRecipeToGroceryListResult | null> => {
      const list = getCachedList(listId);
      if (!list) return null;
      const ingredientStrings =
        scaledIngredientStrings &&
        scaledIngredientStrings.length === selectedIngredientIndices.length
          ? scaledIngredientStrings
          : selectedIngredientIndices
              .filter((i) => i >= 0 && i < recipe.ingredients.length)
              .map((i) => recipe.ingredients[i]);
      if (ingredientStrings.length === 0) return null;

      const previousItems = [...list.items];
      const { items: aggregatedItems, addedCount, mergedCount } = aggregateIngredients(
        list.items as import('@/utils/ingredientMatcher').GroceryItemForMatch[],
        ingredientStrings,
        recipe.id,
        {
          generateId: () => `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          now: Date.now(),
          categoryForItem: defaultCategoryForItem,
        }
      );
      const items: GroceryItem[] = aggregatedItems.map((item, index) => ({
        ...(item as GroceryItem),
        position: index,
      }));

      if (items.length > MAX_GROCERY_ITEMS_PER_LIST) {
        const e = new Error(
          `This list can have at most ${MAX_GROCERY_ITEMS_PER_LIST} items. Remove some items to add more.`
        );
        throw e;
      }

      const updated = await updateList(listId, { items });
      if (!updated) return null;
      return { addedCount, mergedCount, previousItems };
    },
    [updateList]
  );

  return {
    addRecipeToGroceryList,
    loading: updateLoading,
    error: updateError,
  };
}

/**
 * Mutation: delete a grocery list.
 */
export function useDeleteList() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteList = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase.from('grocery_lists').delete().eq('id', id);
      if (deleteError) throw deleteError;
      invalidateGroceryListsCache();
      removeCachedList(id);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteList, loading, error };
}

/** Input for adding items (id, addedAt, position are set by the hook) */
export type AddGroceryItemInput = Omit<GroceryItem, 'id' | 'addedAt' | 'position'>;

/**
 * Mutation: add items to a grocery list.
 */
export function useAddItems() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const addItems = useCallback(
    async (listId: string, newItems: AddGroceryItemInput[]): Promise<GroceryList | null> => {
      if (newItems.length === 0) return getCachedList(listId);

      const current = getCachedList(listId);
      const existingItems = current?.items ?? [];
      if (existingItems.length + newItems.length > MAX_GROCERY_ITEMS_PER_LIST) {
        const e = new Error(
          `This list can have at most ${MAX_GROCERY_ITEMS_PER_LIST} items. Remove some items to add more.`
        );
        setError(e);
        throw e;
      }

      setLoading(true);
      setError(null);
      try {
        const now = Date.now();
        const startPosition = existingItems.length;
        const itemsToAdd: GroceryItem[] = newItems.map((item, i) => ({
          ...item,
          id: `item-${now}-${i}-${Math.random().toString(36).slice(2, 9)}`,
          addedAt: now,
          position: startPosition + i,
        }));
        const items = [...existingItems, ...itemsToAdd];

        const payload = listToRowPayload({ items });
        const { data, error: updateError } = await supabase
          .from('grocery_lists')
          .update(payload)
          .eq('id', listId)
          .select()
          .single();

        if (updateError) throw updateError;
        const updated = data ? rowToList(data as GroceryListRow) : null;
        if (updated) {
          setCachedList(updated);
          updateCachedListInLists(updated);
        }
        return updated;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { addItems, loading, error };
}

/**
 * Mutation: update a single item in a grocery list.
 */
export function useUpdateItem() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateItem = useCallback(
    async (
      listId: string,
      itemId: string,
      updates: Partial<Omit<GroceryItem, 'id' | 'addedAt'>>
    ): Promise<GroceryList | null> => {
      setLoading(true);
      setError(null);
      try {
        const current = getCachedList(listId);
        if (!current) throw new Error('List not found');
        const items = current.items.map((item) =>
          item.id === itemId ? { ...item, ...updates } : item
        );
        const payload = listToRowPayload({ items });
        const { data, error: updateError } = await supabase
          .from('grocery_lists')
          .update(payload)
          .eq('id', listId)
          .select()
          .single();

        if (updateError) throw updateError;
        const updated = data ? rowToList(data as GroceryListRow) : null;
        if (updated) {
          setCachedList(updated);
          updateCachedListInLists(updated);
        }
        return updated;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { updateItem, loading, error };
}

/**
 * Mutation: remove an item from a grocery list.
 */
export function useDeleteItem() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteItem = useCallback(
    async (listId: string, itemId: string): Promise<GroceryList | null> => {
      setLoading(true);
      setError(null);
      try {
        const current = getCachedList(listId);
        if (!current) throw new Error('List not found');
        const items = current.items.filter((item) => item.id !== itemId);
        const payload = listToRowPayload({ items });
        const { data, error: updateError } = await supabase
          .from('grocery_lists')
          .update(payload)
          .eq('id', listId)
          .select()
          .single();

        if (updateError) throw updateError;
        const updated = data ? rowToList(data as GroceryListRow) : null;
        if (updated) {
          setCachedList(updated);
          updateCachedListInLists(updated);
        }
        return updated;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { deleteItem, loading, error };
}
